import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { ClientVMSSubInfo, R2RLayout, R2RSection, TalentVMSSubInfo } from "../../../../typings";
import { VmsSubmittalApiService } from "../vms-submittal-api.service";
import { PageService } from "../shared/page-service";
import { ActivityType, R2RSectionType, RightToRepresentType, VmsSubmittalPage } from "../../../enums";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-vms-submittal-right-to-represent',
  templateUrl: './vms-submittal-right-to-represent.component.html',
  styleUrl: './vms-submittal-right-to-represent.component.scss',
  standalone: false
})
export class VmsSubmittalRightToRepresentComponent implements OnInit, OnDestroy {

  @Input() clientVMSSubInfo: ClientVMSSubInfo;
  showBackButton: boolean = false;
  talentVMSSubInfo: TalentVMSSubInfo;
  saving: boolean = false;
  gatherUUID: string;
  @Output() pageLoading = new EventEmitter<boolean>();

  r2rForm: FormGroup;
  parsedLayout: R2RLayout | null = null;
  private rawLayoutJson: string | undefined;
  readonly R2RSectionType = R2RSectionType;

  private readonly destroy$ = new Subject<void>();

  constructor(private pageService: PageService,
              private router: Router,
              private route: ActivatedRoute,
              private talentHappyPathDataService: TalentHappyPathDataService,
              private vmsSubApiService: VmsSubmittalApiService,
              private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.gatherUUID = this.route.snapshot.queryParams['gatheruuid'];
    if (this.isFromGather()) {
      if (!this.clientVMSSubInfo.vmsSubGatherInfo.rightToRepresentRequired) {
        this.nextPage();
      }
    }

    this.initForm();

    this.vmsSubApiService.getTalentVMSSubInfo(this.talentHappyPathDataService.orderId, this.talentHappyPathDataService.talent.personId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(vmsSubInfo => {
        this.talentVMSSubInfo = vmsSubInfo;
        this.rawLayoutJson = vmsSubInfo.r2rLayout;
        this.parsedLayout = this.parseLayout(vmsSubInfo.r2rLayout);
        if (vmsSubInfo.r2rSigned) {
          this.nextPage();
        }
        this.sanitizeR2rAgreement();
        this.initForm();
        this.pageLoading.emit(false);
      }, err => {
        console.error('Failed to load TalentVMSSubInfo', err);
        this.pageLoading.emit(false);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private parseLayout(raw: string | undefined): R2RLayout | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as R2RLayout).sections)) {
        console.error('Invalid r2rLayout JSON structure; falling back to legacy R2R rendering.', parsed);
        return null;
      }
      const layout = parsed as R2RLayout;
      this.walkSections(layout.sections, s => {
        if ((s.type === R2RSectionType.CHECKBOX || s.type === R2RSectionType.SIGNATURE_INPUT
            || s.type === R2RSectionType.TEXT_INPUT) && !s.fieldKey) {
          console.warn(`R2R layout: ${s.type} section is missing fieldKey; control will not render.`, s);
        }
      });
      return layout;
    } catch (e) {
      console.error('Failed to parse r2rLayout JSON; falling back to legacy R2R rendering.', e);
      return null;
    }
  }

  initForm() {
    if (this.hasLayout()) {
      const controls: Record<string, any> = {};
      this.walkSections(this.parsedLayout!.sections, section => {
        if (!section.fieldKey) return;
        if (section.type === R2RSectionType.CHECKBOX) {
          controls[section.fieldKey] = [false, section.required ? Validators.requiredTrue : null];
        } else if (section.type === R2RSectionType.SIGNATURE_INPUT) {
          const validators = section.required ? [Validators.required, Validators.minLength(2)] : [];
          controls[section.fieldKey] = ['', validators];
        } else if (section.type === R2RSectionType.TEXT_INPUT) {
          const validators = section.required ? [Validators.required] : [];
          controls[section.fieldKey] = ['', validators];
        }
      });
      this.r2rForm = this.fb.group(controls);
    } else {
      this.r2rForm = this.fb.group({
        termsAccepted: [false, this.clientVMSSubInfo?.r2rTypeId > 0 ? Validators.requiredTrue : null]
      });
    }
  }

  private walkSections(sections: R2RSection[], visit: (s: R2RSection) => void): void {
    for (const section of sections) {
      visit(section);
      if (section.children?.length) {
        this.walkSections(section.children, visit);
      }
    }
  }

  hasLayout(): boolean {
    return this.parsedLayout != null;
  }

  get jobDescription(): string {
    return this.talentHappyPathDataService.postingInfo?.jobDescription ?? '';
  }

  get jobTitle(): string {
    return this.talentHappyPathDataService.postingInfo?.jobTitle ?? '';
  }

  goToNextPage() {
    this.saving = true;
    if (this.hasLayout()) {
      let hasJobDescriptionSection = false;
      this.walkSections(this.parsedLayout!.sections, s => {
        if (s.type === R2RSectionType.EXPANDABLE_JOB_DESCRIPTION) {
          hasJobDescriptionSection = true;
        }
      });
      if (hasJobDescriptionSection) {
        this.talentVMSSubInfo.r2rJobDescription = this.jobDescription;
      }
      this.captureLegalFields();
      this.talentVMSSubInfo.r2rAgreement = this.composeSnapshot();
    }
    this.vmsSubApiService.saveTalentVMSSubInfo(this.talentVMSSubInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.saving = false;
        this.nextPage();
      }, () => {
        console.error("Error saving VMS");
        this.saving = false;
      });
  }
  // Preserve just the legal document body for display in the downloadable PDF.
  // sanitizeR2rAgreement() (called at ngOnInit) already substituted [CANDIDATENAME] into
  // parsedLayout.sections with the talent's person name for the form UI, but for the snapshot
  // we want the legal name the talent typed on this page when the R2R type is Apple US VMS.
  // Re-parse the original layout so [CANDIDATENAME] is present again and substitute freshly
  // using the signer name resolver.
  private composeSnapshot(): string {
    const originalLayout = this.rawLayoutJson ? this.parseLayout(this.rawLayoutJson) : null;
    const sections = originalLayout?.sections ?? this.parsedLayout?.sections ?? [];
    const substituted = this.substituteForSnapshot(sections);
    const isVictoriaSecret = this.clientVMSSubInfo?.r2rTypeId === RightToRepresentType.VICTORIA_SECRET;
    const rootClass = isVictoriaSecret ? 'r2r-snapshot r2r-type-victoria-secret' : 'r2r-snapshot';
    const parts: string[] = [`<div class="${rootClass}">`];
    const flat: R2RSection[] = [];
    this.walkSections(substituted, s => flat.push(s));
    for (const section of flat) {
      const copy = section.copy ?? '';
      switch (section.type) {
        case R2RSectionType.AGREEMENT_TEXT:
          parts.push(
            `<p class="c7"><span class="c3 c2"><b>Representation Agreement</b></span></p>`,
            this.renderCopyBlock(copy)
          );
          break;
        case R2RSectionType.AUTHORIZATION_STATEMENT:
          parts.push(
            `<p class="c1"><span class="c3 c2"></span></p>`,
            `<p class="c7"><span class="c3 c2"><b>Candidate Authorization Statement:</b></span></p>`,
            this.renderCopyBlock(copy)
          );
          break;
      }
    }

    parts.push(`</div>`);
    return parts.join('');
  }

  // Renders a block of copy for the snapshot. If the copy already contains <p> tags, it preserves that HTML and adds class="c7" to any <p> tags that do not already have a class; otherwise it wraps the text in <p class="c7"><span class="c2 c3">…</span></p>.
  private renderCopyBlock(copy: string): string {
    const trimmed = copy.trim();
    if (!trimmed) {
      return '';
    }
    if (/<p(?=[\s>])/i.test(trimmed)) {
      return trimmed.replace(/<p(?=[\s>])(?![^>]*\bclass\s*=)/gi, '<p class="c7"');
    }
    return `<p class="c7"><span class="c2 c3">${trimmed}</span></p>`;
  }

  private substituteForSnapshot(sections: R2RSection[]): R2RSection[] {
    const escapeHtml = (value: string): string => value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const signerName = escapeHtml(this.resolveSignerName());
    const positionTitle = escapeHtml(this.talentHappyPathDataService.postingInfo?.jobTitle ?? '');
    const vmsClientName = escapeHtml(this.clientVMSSubInfo?.vmsClientName ?? '');
    const substitute = (s: string | null | undefined): string | undefined => s
      ?.replace(/\[CANDIDATENAME\]/g, signerName)
      ?.replace(/\[POSITIONTITLE\]/g, positionTitle)
      ?.replace(/\[VMSCLIENTNAME\]/g, vmsClientName);
    const walk = (list: R2RSection[]): R2RSection[] => list.map(section => ({
      ...section,
      copy: section.copy ? substitute(section.copy) : section.copy,
      title: section.title ? substitute(section.title) : section.title,
      subtitle: section.subtitle ? substitute(section.subtitle) : section.subtitle,
      children: section.children ? walk(section.children) : section.children,
    }));
    return walk(sections);
  }

  // Returns the name that should appear in the saved snapshot's [CANDIDATENAME] placeholder.
  // Apple US VMS: legal first + last if both are typed on this page; else fall back to the
  // person's name using the same preferred/first + last derivation as sanitizeR2rAgreement.
  private resolveSignerName(): string {
    const formValue = this.r2rForm?.value ?? {};
    const legalFirst = typeof formValue.legalFirstName === 'string' ? formValue.legalFirstName.trim() : '';
    const legalLast = typeof formValue.legalLastName === 'string' ? formValue.legalLastName.trim() : '';
    if (this.clientVMSSubInfo?.r2rTypeId === RightToRepresentType.APPLE_VMS && legalFirst && legalLast) {
      return `${legalFirst} ${legalLast}`;
    }
    const talent = this.talentHappyPathDataService.talent;
    const preferred = talent.preferredName?.trim() || '';
    const first = talent.firstName?.trim() || '';
    const last = talent.lastName?.trim() || '';
    const givenName = preferred || first;
    const surname = (preferred && last && preferred.includes(last)) ? '' : last;
    return [givenName, surname].filter(Boolean).join(' ');
  }

  // Copies the captured Legal Name / Legal Address field values onto talentVMSSubInfo before save.
  // Field keys match the section fieldKeys in the Apple US VMS layout JSON.
  private captureLegalFields(): void {
    const value = this.r2rForm.value;
    const read = (key: string): string | undefined => {
      const v = value[key];
      const trimmed = typeof v === 'string' ? v.trim() : '';
      return trimmed ? trimmed : undefined;
    };

    this.talentVMSSubInfo.legalFirstName = read('legalFirstName');
    this.talentVMSSubInfo.legalLastName = read('legalLastName');

    const addressKeys = ['address1', 'address2', 'city', 'state', 'postalCode'];
    if (addressKeys.some(key => key in value)) {
      this.talentVMSSubInfo.legalAddress = {
        address1: read('address1'),
        address2: read('address2'),
        city: read('city'),
        state: read('state'),
        postalCode: read('postalCode'),
      };
    }
  }

  goToPreviousPage() {
  }

  disableSubmit() {
    return this.r2rForm.invalid || this.saving;
  }

  sanitizeR2rAgreement() {
    const talent = this.talentHappyPathDataService.talent;
    const preferred = talent.preferredName?.trim() || '';
    const first = talent.firstName?.trim() || '';
    const last = talent.lastName?.trim() || '';

    const givenName = preferred || first;
    const surname = (preferred && last && preferred.includes(last)) ? '' : last;
    const candidateName = [givenName, surname].filter(Boolean).join(' ');
    const positionTitle = this.talentHappyPathDataService.postingInfo?.jobTitle ?? '';
    const vmsClientName = this.clientVMSSubInfo?.vmsClientName ?? '';

    const substitute = (s: string | null | undefined): string | undefined => s
      ?.replace(/\[CANDIDATENAME\]/g, candidateName)
      ?.replace(/\[POSITIONTITLE\]/g, positionTitle)
      ?.replace(/\[VMSCLIENTNAME\]/g, vmsClientName);

    if (this.talentVMSSubInfo.r2rAgreement) {
      this.talentVMSSubInfo.r2rAgreement = substitute(this.talentVMSSubInfo.r2rAgreement)!;
    }
    if (this.parsedLayout) {
      const substituteTree = (sections: R2RSection[]): R2RSection[] =>
        sections.map(section => ({
          ...section,
          copy: section.copy ? substitute(section.copy) : section.copy,
          title: section.title ? substitute(section.title) : section.title,
          subtitle: section.subtitle ? substitute(section.subtitle) : section.subtitle,
          children: section.children ? substituteTree(section.children) : section.children,
        }));
      this.parsedLayout.sections = substituteTree(this.parsedLayout.sections);
    }
  }

  nextPage(): void {
    if (this.isFromGather()) {
      // The only way one of the sections can be required when a candidate has arrived from a gather
      // is if one of the sections is chosen for VMS data collection.
      // If a candidate has arrived from a gather, the VMS data collection section can be skipped, even when the data is required for
      // VMS data collection, if the vmsSubGatherInfo.whatever is false
      if (this.clientVMSSubInfo.vmsSubGatherInfo.candidateValidationRequired) {
        // We know at least one of the VMS candidate validation options has been selected
        // We also know the Send Via Gather Interested 'Yes' option has been selected for Candidate Validation
        this.pageService.setActivePage(VmsSubmittalPage.CANDIDATE_VALIDATION);
      } else if (this.clientVMSSubInfo.vmsSubGatherInfo.employmentHistoryRequired ||
        this.clientVMSSubInfo.vmsSubGatherInfo.relationshipDisclosureRequired ||
        this.clientVMSSubInfo.ernstAndYoungEmploymentHistoryRequired) {
        // We know at least one of the VMS Employment History With Client options has been selected
        // We also know the Send Via Gather Interested 'Yes' option has been selected for Employment History With Client
        this.pageService.setActivePage(VmsSubmittalPage.EMPLOYMENT_HISTORY_AND_RELATIONSHIP_DISCLOSURE);
      } else {
        this.router.navigateByUrl('/initial-interview?gatheruuid=' + this.gatherUUID, { skipLocationChange: true });
      }
    } else {
      if (this.clientVMSSubInfo.dobRequired || this.clientVMSSubInfo.taxIdRequired) {
        this.pageService.setActivePage(VmsSubmittalPage.CANDIDATE_VALIDATION);
      } else if (Object.values(this.clientVMSSubInfo.clientEmploymentHistory).some(value => value === true)
        || this.clientVMSSubInfo.relationshipDisclosureRequired || this.clientVMSSubInfo.ernstAndYoungEmploymentHistoryRequired) {
        this.pageService.setActivePage(VmsSubmittalPage.EMPLOYMENT_HISTORY_AND_RELATIONSHIP_DISCLOSURE);
      } else {
        this.router.navigate(['thank-you'], { queryParams: { from: 'vms-submittal' }, skipLocationChange: true });
      }
    }
  }

  doThisLater() {
    this.vmsSubApiService.createActivity(
      ActivityType.VMS_SUBMITTAL_DO_THIS_LATER_CLICKED, this.talentHappyPathDataService.talent.personId, this.talentHappyPathDataService.orderId, this.talentHappyPathDataService.postingId
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          console.log('Vms submittal do this later activity created successfully.');
        },
        error: (err) => {
          console.error('Failed to create vms submittal do this later activity :', err);
        }
      });

    this.router.navigateByUrl('/initial-interview?gatheruuid=' + this.gatherUUID, { skipLocationChange: true });
  }

  isFromGather(): boolean {
    return this.talentHappyPathDataService.gather != null && this.talentHappyPathDataService.gather.gatherId != null;
  }
}
